import { useContext, useState, useEffect, useMemo, useRef, useCallback } from "react"
import { BrowserRouter as Router } from "react-router-dom"

import axios from "axios"

import {
  ApolloClient,
  InMemoryCache,
  ApolloProvider,
  HttpLink,
  useLazyQuery,
} from "@apollo/client"

import { useQuery } from "@apollo/client"
import { setContext } from "@apollo/client/link/context"

// Context
import { UserContext } from "./context/userContext"
import { FirebaseContext } from "./firebase"
import { UserActivityContext } from "./context/userActivityContext"

// Comps
import RoutesBuilder from "./components/Routes"
import LoaderFullPage from "./components/Loaders"

// Pages
import Login from "./pages/Login/"
import endpoint from "./config/endpoint"

// Utils
import { LogError, LogInfo, LogWarning } from "./helpers/logger"
import { ConfirmDialog } from "primereact/confirmdialog"
import {
  GET_MICROSERVICE_TOKENS,
  GET_MICROSERVICES_BY_ORGANIZATION,
  GET_PARTICIPANT,
} from "./queries/app_queries"
import { getMicroserviceStaffId } from "./config/microservicesSystemUsers"
import { useActivityTracker } from "./hooks/activityTracker/useActivityTracker"
import UserActivityWarning from "./components/UserActivityWarning"
import ROUTES from "./components/Routes/routesContants"
import useTokenManager, {
  getUserToken,
} from "./hooks/tokenManager/useTokenManager"

function HandleIsAuth({ participantId, token, authUser, handleMainTokenRefresh }) {
  const [loadingParticipantData, setLoadingParticipantData] = useState(false)
  const { refetch: refetchParticipant } = useQuery(GET_PARTICIPANT, {
    variables: {
      participant_id: participantId,
    },
    skip: true,
  })

  const [participantData, setParticipantData] = useState(null)
  const [orgEnabledMicroservices, setOrgEnabledMicroservices] = useState({})

  const InactiveParticipantStatuses = [
    "Inactive Discharged",
    "Inactive Referrals",
    "Kiosk Member",
  ]

  //Manages token lifetimes and refreshing
  const {
    initialTokenFetchDone,
    getMainToken,
    getNotificationToken,
    getTaskToken,
    getDocumentSignToken,
    getAppointmentsToken,
    getTelerecoveryToken,
    getAssessmentsToken,
    getGooglePlacesKey,
  } = useTokenManager(token, authUser, handleMainTokenRefresh)

  //Tracks user activity and handles automatic logout after inactivity.
  const {
    state,
    countdown,
    getLogoutAfter,
    getWarningDuration,
    getLastActive,
    resetTimer,
    logout,
  } = useActivityTracker({
    doMonitor: !!participantId,
    onWarning: () => {},
  })

  const userActivityContextValue = useMemo(
    () => ({
      getLogoutAfter,
      getWarningDuration,
      state,
      countdown,
      getLastActive,
      resetTimer,
      logout,
    }),
    [
      getLogoutAfter,
      getWarningDuration,
      state,
      countdown,
      getLastActive,
      resetTimer,
      logout,
    ],
  )

  const [
    GetMicroservicesByOrganization,
    { variables: lastMicroserviceQueryVars },
  ] = useLazyQuery(GET_MICROSERVICES_BY_ORGANIZATION, {
    fetchPolicy: "network-only",
    onCompleted: (data) => {
      if (data?.getMicroservicesByOrganization) {
        setOrgEnabledMicroservices((prevState) => ({
          ...prevState,
          [lastMicroserviceQueryVars.organization_id]:
            data.getMicroservicesByOrganization,
        }))
      }
    },
    onError: (error) => {
      LogError("Error when fetching microservices by organization", error)
      LogInfo("Last microservice query vars:", lastMicroserviceQueryVars)
    },
  })

  async function fetchOrgMicroservices() {
    for (const org of participantData.participant_organizations) {
      const staffId = getMicroserviceStaffId()
      const organizationId = org.organization_id

      if (
        !organizationId ||
        typeof organizationId !== "string" ||
        organizationId.trim() === ""
      ) {
        LogError(
          "Invalid organization_id for fetching microservices:",
          organizationId,
        )
        continue
      }

      await GetMicroservicesByOrganization({
        variables: {
          staff_id: staffId,
          staff_organization_id: organizationId,
          organization_id: organizationId,
        },
      })
    }
  }

  useEffect(() => {
    if (participantData) {
      fetchOrgMicroservices()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participantData])

  function isMicroserviceEnabled(microservice_id, organization_id) {
    if (!organization_id) {
      // Check for any organization where the microservice is enabled
      for (const orgId in orgEnabledMicroservices) {
        const orgMicroservices = orgEnabledMicroservices[orgId]

        if (orgMicroservices) {
          // eslint-disable-next-line eqeqeq
          const microservice = orgMicroservices.find(
            (ms) => ms.microservices_id == microservice_id,
          )
          if (microservice && microservice.microservices_enabled) {
            return true
          }
        }
      }

      return false
    } else {
      // If organization_id is provided, check only for that organization
      const orgMicroservices = orgEnabledMicroservices[organization_id]
      if (!orgMicroservices) {
        return false
      }

      const microservice = orgMicroservices.find(
        (ms) => ms.microservices_id === microservice_id,
      )
      return microservice ? microservice.microservices_enabled : false
    }
  }

  // Flatten staff assignments and inject organization_id
  function flattenStaffAssignments(participants) {
    return participants.flatMap((ptp) =>
      (ptp.staff_assigment_list || []).map((staff) => ({
        ...staff,
        displayName: `${staff.staff_name_first} ${staff.staff_name_last}`,
        organization_id: ptp.organization_id,
        organization_alias: ptp.organization_alias,
        organization_name: ptp.organization_name,
      })),
    )
  }

  const refetchParticipantData = () => {
    setLoadingParticipantData(true)
    refetchParticipant()
      .then(({ data }) => {
        if (data && data.participant && data.participant.length > 0) {
          // Clean up data from the server
          const participantData = {
            // SHARED VALUES
            participant_id: data.participant[0].participant_id,
            participant_alias: data.participant[0].participant_alias,
            participant_name_first: data.participant[0].participant_name_first,
            participant_name_last: data.participant[0].participant_name_last,
            participant_create_date:
              data.participant[0].participant_create_date,
            participant_email_account:
              data.participant[0].participant_email_account,
            participant_profile_image:
              data.participant[0].participant_profile_image,
            staff_role: data.participant[0].staff_role, // i.e. Participant role
            staff_role_name: data.participant[0].staff_role_name, // i.e. Participant role name
            staff_assignment_list: flattenStaffAssignments(data.participant),

            // ORG-SPECIFIC VALUES
            participant_organizations: [],
          }

          // POPULATE ORG-SPECIFIC VALUES
          participantData.participant_organizations = data.participant.map(
            (ptp) => ({
              organization_id: ptp.organization_id,
              organization_alias: ptp.organization_alias,
              organization_name: ptp.organization_name,
              enterprise_id: ptp.enterprise_id,
              enterprise_alias: ptp.enterprise_alias,
              participant_membership_id: ptp.participant_membership_id,
              participant_membership_status: ptp.participant_membership_status,
              staff_assignment_list: ptp.staff_assigment_list,
            }),
          )

          setParticipantData(participantData)
        } else {
          // Handle case where no participant data is returned
          LogError("No participant data returned from the server.")
        }
      })
      .catch((error) => {
        LogError("Error fetching participant:", error)
      })
      .finally(() => {
        setLoadingParticipantData(false)
      })
  }

  useEffect(() => {
    refetchParticipantData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const checkOrgPropWithFallback = (propName, propValue, sourceComponent) => {
    if (!propValue && participantData?.participant_organizations?.length > 0) {
      // If value is not provided, use the first organization from participant_organizations
      const fallbackOrg = participantData.participant_organizations[0]
      LogWarning(
        `${sourceComponent} makes use of ${propName}, but a value was not provided, falling back to participant_organizations[0].${propName} (${fallbackOrg[propName]}).`,
      )
      return fallbackOrg[propName]
    }

    return propValue // Return the provided value if it exists
  }

  const isParticipantInactive = (organization_id) => {
    return InactiveParticipantStatuses.includes(
      participantData?.participant_organizations?.find(
        (org) => org.organization_id === organization_id,
      )?.participant_membership_status,
    )
  }

  //System notifications
  const [notification_error, setNotificationError] = useState(undefined)
  const [notification_action, setNotificationAction] = useState(undefined)
  const [notification_Information, setNotificationInformation] =
    useState(undefined)
  const [mutedNotifications, setMutedNotifications] = useState(null)

  //Tasks related state items
  const [tasksDialogVisible, setTasksDialogVisible] = useState(false)
  const [pendingTasksCount, setPendingTasksCount] = useState(0)

  if (!participantData || !initialTokenFetchDone) return <LoaderFullPage />

  const ctxValue = {
    refetchParticipantData,
    loadingParticipantData,
    ...participantData,
    checkOrgPropWithFallback,
    isParticipantInactive,
    isMicroserviceEnabled,
    notification_error,
    setNotificationError,
    notification_Information,
    setNotificationInformation,
    notification_action,
    setNotificationAction,
    mutedNotifications,
    setMutedNotifications,
    tasksDialogVisible,
    setTasksDialogVisible,
    pendingTasksCount,
    setPendingTasksCount,
    tokens: {
      getMainToken,
      getAppointmentsToken,
      getDocumentSignToken,
      getNotificationToken,
      getTaskToken,
      getTelerecoveryToken,
      getAssessmentsToken,
    },
    keys: {
      getGooglePlacesKey,
    },
  }

  return (
    <UserContext.Provider value={ctxValue}>
      <UserActivityContext.Provider value={userActivityContextValue}>
        <UserActivityWarning />
        <RoutesBuilder />
      </UserActivityContext.Provider>
    </UserContext.Provider>
  )
}

function App() {
  const [fetching, setFetching] = useState(false)
  const [user, setUser] = useState(null)
  const [token, setToken] = useState(null)
  const [errorLogin, setErrorLogin] = useState(null)
  const [failedLoginAttempts, setFailedLoginAttempts] = useState(0)

  const firebase = useContext(FirebaseContext)

  //Use this for Apollo client to track the latest token without rerendering the entire App
  const latestTokenRef = useRef(null)

  //This callback will be passed to useTokenManager to update the token whenever it is refreshed, without causing a full re-render of the App component.
  const handleMainTokenRefresh = useCallback((newToken) => {
    latestTokenRef.current = newToken
  }, [])


  const incrementFailedLoginAttempts = () => {
    if (failedLoginAttempts < 2) {
      setErrorLogin({
        message: "Invalid email or password. Please try again.",
        email: "",
      })
    } else {
      setErrorLogin(null)
    }
    setFailedLoginAttempts((prev) => prev + 1)
  }

  useState(() => {
    firebase.auth.onAuthStateChanged(async (user) => {
      const getToken = async () => {
        try {
          setFetching(true)

          /*Google Analytics tracking*/
          window.dataLayer.push({
            event: "login_participant",
            participant_id: user?.uid,
            user_role: 100,
          })
          /*Google Analytics tracking*/

          const newToken = await getUserToken(user)
          latestTokenRef.current = newToken
          setToken(newToken)
        } catch (err) {
          setToken(null)
        } finally {
          setFetching(false)
        }
      }
      user && getToken()

      return user ? setUser(user) : setUser(null)
    })
  }, [])

  const sendToLogin = !user || !token

  const createApolloClient = () => {
    const setAuthorizationLink = setContext(() => ({
      headers: { Authorization: latestTokenRef.current },
    }))
    const httpLink = new HttpLink({ uri: `${endpoint}/graphql` })
    return new ApolloClient({
      cache: new InMemoryCache(),
      link: setAuthorizationLink.concat(httpLink),
    })
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const apolloClient = useMemo(() => createApolloClient(), [token])

  if (fetching) return <LoaderFullPage />

  if (sendToLogin)
    return (
      <Login
        setFetching={setFetching}
        incrementFailedAttempts={incrementFailedLoginAttempts}
        showResetPasswordPrompt={failedLoginAttempts >= 3}
        errorLogin={errorLogin}
      />
    )

  return (
    <ApolloProvider client={apolloClient}>
      <Router>
        <ConfirmDialog />
        <HandleIsAuth participantId={user.uid} token={token} authUser={user} handleMainTokenRefresh={handleMainTokenRefresh}   />
      </Router>
    </ApolloProvider>
  )
}

export default App
